// Базовые компоненты. Внешний вид — в base.css, здесь только разметка и доступность.
import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { dismissToast, useToasts } from '../toast.js';

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'success' | 'danger-soft';

export function Button({
  variant = 'default',
  size,
  block,
  icon,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'lg';
  block?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      type={type}
      className={clsx(
        'btn',
        variant !== 'default' && `btn-${variant}`,
        size && `btn-${size}`,
        block && 'btn-block',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type={type} className={clsx('icon-btn', className)} aria-label={label} title={label} {...rest}>
      {children}
    </button>
  );
}

export function Card({
  title,
  count,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={clsx('card', className)}>
      {(title || action) && (
        <header className="card-head">
          {title && (
            <h2 className="card-title">
              {title}
              {count !== undefined && <span className="count">{count}</span>}
            </h2>
          )}
          {action}
        </header>
      )}
      <div className={clsx('card-body', bodyClassName)}>{children}</div>
    </section>
  );
}

export type Tone = 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export function Chip({
  tone = 'default',
  dot,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={clsx('chip', tone !== 'default' && `chip-${tone}`, dot && 'chip-dot')}>{children}</span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd>{children}</kbd>;
}

export function Meter({ value, tone, label }: { value: number; tone?: 'warn' | 'danger'; label: string }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    // biome-ignore lint/a11y/useSemanticElements: у <meter> нет единого стиля в браузерах, роль даёт ту же доступность
    <div
      className="meter"
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(v * 100)}
    >
      <i className={tone} style={{ width: `${v * 100}%` }} />
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode; icon?: ReactNode }>;
  label: string;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: fieldset ломает раскладку inline-flex в Safari
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Skeleton({ w, h = 14, className }: { w?: number | string; h?: number; className?: string }) {
  return (
    <div className={clsx('skeleton', className)} style={{ width: w ?? '100%', height: h }} aria-hidden />
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label = 'Загрузка' }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function Toasts() {
  const items = useToasts();
  return (
    <div className="toast-host" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className="toast" role="status">
          <span>{t.text}</span>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action?.run();
                dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
